import {
    Badge,
    Box,
    Button,
    ButtonGroup,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Select,
    SimpleGrid,
    Tag,
    TagCloseButton,
    TagLabel,
    Text,
    Wrap,
    WrapItem,
} from "@chakra-ui/react";
import type { ColumnFiltersState } from "@tanstack/react-table";
import { useCallback, type ReactNode } from "react";
import { CandidateQualification, CandidateStatus, DesignatedZoneType, displayQualification, displayZone } from "../../api/models/EntityCandidate";
import { DateFilter, isDateFilterActive, type DateFilterValue } from "./DateFilter";
import { TextFilter, isTextFilterActive, type TextFilterValue } from "./TextFilter";

interface CandidatesFiltersProps {
    columnFilters: ColumnFiltersState;
    onFiltersChange: (filters: ColumnFiltersState) => void;
    onClearAll: () => void;
}

function getTextFilter(filters: ColumnFiltersState, id: string): TextFilterValue | undefined {
    const raw = filters.find(f => f.id === id)?.value;
    if (!raw) return undefined;
    if (typeof raw === "string") return { mode: "contains", value: raw };
    return raw as TextFilterValue;
}

function getSetFilter(filters: ColumnFiltersState, id: string): string[] {
    return (filters.find(f => f.id === id)?.value as string[] | undefined) ?? [];
}

function getDateFilter(filters: ColumnFiltersState, id: string): DateFilterValue | undefined {
    return filters.find(f => f.id === id)?.value as DateFilterValue | undefined;
}

function setFilter(filters: ColumnFiltersState, id: string, value: unknown): ColumnFiltersState {
    const next = filters.filter(f => f.id !== id);
    if (value === "" || value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
        return next;
    }
    return [...next, { id, value }];
}

const ALL_STATUSES = Object.values(CandidateStatus);
const ALL_QUALIFICATIONS = Object.values(CandidateQualification);

/** Wraps a filter field so an active filter gets a colored left border + light background tint, making it easy to scan which fields are constraining the list. */
function FilterField({ isActive, children }: { isActive: boolean; children: ReactNode }) {
    return (
        <Box
            borderLeftWidth="3px"
            borderLeftColor={isActive ? "blue.400" : "transparent"}
            bg={isActive ? "blue.50" : undefined}
            _dark={{ bg: isActive ? "blue.900" : undefined }}
            borderRadius="md"
            px={2}
            py={1}
        >
            {children}
        </Box>
    );
}

/** Section title with a small badge showing how many fields in this section currently have an active filter. */
function SectionHeader({ label, activeCount }: { label: string; activeCount: number }) {
    return (
        <Flex align="center" gap={2} mb={2}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                {label}
            </Text>
            {activeCount > 0 && (
                <Badge colorScheme="blue" borderRadius="full" fontSize="0.6rem" px={2}>
                    {activeCount}
                </Badge>
            )}
        </Flex>
    );
}

/** Small dot shown next to a FormLabel for fields (Select-based) that don't render their own label internally. */
function FieldLabel({ label, isActive }: { label: string; isActive: boolean }) {
    return (
        <FormLabel fontSize="xs" mb={1} display="flex" alignItems="center" gap={1}>
            {label}
            {isActive && <Box boxSize="6px" borderRadius="full" bg="blue.500" aria-label="Filter active" />}
        </FormLabel>
    );
}

export function CandidatesFilters({ columnFilters, onFiltersChange, onClearAll }: CandidatesFiltersProps) {
    const set = useCallback((id: string, value: unknown) => onFiltersChange(setFilter(columnFilters, id, value)), [columnFilters, onFiltersChange]);

    const setDateFilter = useCallback(
        (id: string, value: DateFilterValue | undefined) => {
            // Always store the value so the mode selection persists even before a date is entered.
            // useCandidatesPage skips sending the API filter when no date is set.
            onFiltersChange(setFilter(columnFilters, id, value ?? undefined));
        },
        [columnFilters, onFiltersChange]
    );

    const toggleSetValue = useCallback(
        (id: string, value: string) => {
            const current = getSetFilter(columnFilters, id);
            const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
            onFiltersChange(setFilter(columnFilters, id, next));
        },
        [columnFilters, onFiltersChange]
    );

    const hasFilters = columnFilters.length > 0;

    // Per-field active state, used for both the field highlight/dot and the section badge counts.
    const isLastNameActive = isTextFilterActive(getTextFilter(columnFilters, "lastName"));
    const isFirstNameActive = isTextFilterActive(getTextFilter(columnFilters, "firstName"));
    const isExternalIdActive = isTextFilterActive(getTextFilter(columnFilters, "externalId"));
    const isStatusActive = getSetFilter(columnFilters, "status").length > 0;
    const isZoneActive = getSetFilter(columnFilters, "designatedZoneType").length > 0;
    const isHireDateActive = isDateFilterActive(getDateFilter(columnFilters, "employmentHireDate"));
    const isCompletedOnActive = isDateFilterActive(getDateFilter(columnFilters, "questionnaireCompletedOn"));
    const isQualificationsActive = getSetFilter(columnFilters, "qualifications").length > 0;

    const searchActiveCount = [isLastNameActive, isFirstNameActive, isExternalIdActive].filter(Boolean).length;
    const classificationActiveCount = [isStatusActive, isZoneActive].filter(Boolean).length;
    const datesActiveCount = [isHireDateActive, isCompletedOnActive].filter(Boolean).length;
    const qualificationsActiveCount = isQualificationsActive ? 1 : 0;

    return (
        <Box borderWidth={1} borderRadius="md" p={4} bg="white" _dark={{ bg: "gray.800" }}>
            {/* Search */}
            <SectionHeader label="Search" activeCount={searchActiveCount} />
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={4}>
                <FilterField isActive={isLastNameActive}>
                    <FormControl size="sm">
                        <TextFilter
                            label="Last Name"
                            value={getTextFilter(columnFilters, "lastName")}
                            onChange={v => set("lastName", v)}
                            isActive={isLastNameActive}
                        />
                    </FormControl>
                </FilterField>
                <FilterField isActive={isFirstNameActive}>
                    <FormControl size="sm">
                        <TextFilter
                            label="First Name"
                            value={getTextFilter(columnFilters, "firstName")}
                            onChange={v => set("firstName", v)}
                            isActive={isFirstNameActive}
                        />
                    </FormControl>
                </FilterField>
                <FilterField isActive={isExternalIdActive}>
                    <FormControl size="sm">
                        <TextFilter
                            label="External ID"
                            value={getTextFilter(columnFilters, "externalId")}
                            onChange={v => set("externalId", v)}
                            isActive={isExternalIdActive}
                        />
                    </FormControl>
                </FilterField>
            </SimpleGrid>

            <Divider mb={4} />

            {/* Classification */}
            <SectionHeader label="Classification" activeCount={classificationActiveCount} />
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={4}>
                <FilterField isActive={isStatusActive}>
                    <FormControl size="sm">
                        <FieldLabel label="Status" isActive={isStatusActive} />
                        <Select
                            size="sm"
                            title="Filter by status"
                            aria-label="Filter by status"
                            value={getSetFilter(columnFilters, "status")[0] ?? ""}
                            onChange={e => set("status", e.target.value ? [e.target.value] : undefined)}
                        >
                            <option value="">All statuses</option>
                            {ALL_STATUSES.map(s => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                </FilterField>
                <FilterField isActive={isZoneActive}>
                    <FormControl size="sm">
                        <FieldLabel label="Designated Zone" isActive={isZoneActive} />
                        <Select
                            size="sm"
                            title="Filter by designated zone"
                            aria-label="Filter by designated zone"
                            value={getSetFilter(columnFilters, "designatedZoneType")[0] ?? ""}
                            onChange={e => set("designatedZoneType", e.target.value ? [e.target.value] : undefined)}
                        >
                            <option value="">All zones</option>
                            {Object.values(DesignatedZoneType).map(z => (
                                <option key={z} value={z}>
                                    {displayZone(z)}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                </FilterField>
            </SimpleGrid>

            <Divider mb={4} />

            {/* Dates */}
            <SectionHeader label="Dates" activeCount={datesActiveCount} />
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={4}>
                <FilterField isActive={isHireDateActive}>
                    <FormControl size="sm">
                        <DateFilter
                            label="Hire Date"
                            value={getDateFilter(columnFilters, "employmentHireDate")}
                            onChange={v => setDateFilter("employmentHireDate", v)}
                            isActive={isHireDateActive}
                        />
                    </FormControl>
                </FilterField>
                <FilterField isActive={isCompletedOnActive}>
                    <FormControl size="sm">
                        <DateFilter
                            label="Questionnaire Completed On"
                            value={getDateFilter(columnFilters, "questionnaireCompletedOn")}
                            onChange={v => setDateFilter("questionnaireCompletedOn", v)}
                            isActive={isCompletedOnActive}
                        />
                    </FormControl>
                </FilterField>
            </SimpleGrid>

            <Divider mb={4} />

            {/* Qualifications */}
            <FilterField isActive={isQualificationsActive}>
                <FormControl size="sm" mb={3}>
                    <Flex align="center" gap={2} mb={2}>
                        <SectionHeader label="Qualifications" activeCount={qualificationsActiveCount} />
                        <ButtonGroup size="xs" isAttached variant="outline">
                            <Button
                                colorScheme={(columnFilters.find(f => f.id === "qualificationMode")?.value ?? "and") === "and" ? "blue" : "gray"}
                                variant={(columnFilters.find(f => f.id === "qualificationMode")?.value ?? "and") === "and" ? "solid" : "outline"}
                                onClick={() => onFiltersChange(setFilter(columnFilters, "qualificationMode", "and"))}
                            >
                                AND
                            </Button>
                            <Button
                                colorScheme={(columnFilters.find(f => f.id === "qualificationMode")?.value ?? "and") === "or" ? "blue" : "gray"}
                                variant={(columnFilters.find(f => f.id === "qualificationMode")?.value ?? "and") === "or" ? "solid" : "outline"}
                                onClick={() => onFiltersChange(setFilter(columnFilters, "qualificationMode", "or"))}
                            >
                                OR
                            </Button>
                        </ButtonGroup>
                    </Flex>
                    <Wrap spacing={1}>
                        {ALL_QUALIFICATIONS.map(q => {
                            const selected = getSetFilter(columnFilters, "qualifications").includes(q);
                            return (
                                <WrapItem key={q}>
                                    <Tag
                                        size="sm"
                                        variant={selected ? "solid" : "outline"}
                                        colorScheme={selected ? "blue" : "gray"}
                                        cursor="pointer"
                                        onClick={() => toggleSetValue("qualifications", q)}
                                    >
                                        {selected && (
                                            <TagCloseButton
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    toggleSetValue("qualifications", q);
                                                }}
                                            />
                                        )}
                                        <TagLabel>{displayQualification(q)}</TagLabel>
                                    </Tag>
                                </WrapItem>
                            );
                        })}
                    </Wrap>
                </FormControl>
            </FilterField>

            <Flex justify="flex-end">
                <Button size="xs" variant="ghost" onClick={onClearAll} isDisabled={!hasFilters}>
                    Clear all filters
                </Button>
            </Flex>
        </Box>
    );
}
